// umlaut-check: skip — "VorschlaegeLieferwoche"/"LkwGruppe" sind Komponenten-
// bzw. Dateinamen (technische Bezeichner), bleiben ASCII.

import React, {useMemo, useState} from "react";
import {Card, OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {PiCaretDown, PiCaretUp} from "react-icons/pi";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import SelectList from "../SelectList.jsx";
import VorschlagZeile from "./VorschlagZeile.jsx";
import LkwLadung from "./LkwLadung.jsx";
import {produktlinieFarbe, produktlinieMarkeLabel, LADUNG_FARBE_GEPLANT} from "./ladungsFarben.js";
import {dringlichkeitLabel, formatDatum, formatFaktor, formatFarbnummer, formatKw, formatMenge, formatProzent} from "./format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

const SORT_OPTIONEN = [
    {value: "dringlichkeit", label: "Dringlichkeit"},
    {value: "top_artikel", label: "Top-Artikel"},
    {value: "menge", label: "Menge (absteigend)"},
    {value: "farbnummer", label: "Farbnummer"},
];

const DRINGLICHKEIT_RANG = {ueberfaellig: 0, sofort: 1, bald: 2, geplant: 3};

/** Sortiert die Artikel EINES LKW (T16: "Sortierung innerhalb eines LKW wählbar, u. a. Top-Artikel"). */
function sortiereArtikel(artikel, sortierung) {
    const liste = [...artikel];
    switch (sortierung) {
        case "top_artikel":
            // Top-Farbton (von Hand markiert) zuerst, danach Verkaufsrang bzw.
            // Jahresverbrauch - dieselbe Regel wie LieferfaehigkeitTabelle.jsx.
            return liste.sort((a, b) => {
                const topA = a.top_farbton ? 1 : 0;
                const topB = b.top_farbton ? 1 : 0;
                if (topA !== topB) {
                    return topB - topA;
                }
                const rangA = a.rang_verkauf ?? null;
                const rangB = b.rang_verkauf ?? null;
                if (rangA !== null && rangB !== null) {
                    return rangA - rangB;
                }
                return (b.verbrauch_jahr ?? Number.NEGATIVE_INFINITY) - (a.verbrauch_jahr ?? Number.NEGATIVE_INFINITY);
            });
        case "menge":
            return liste.sort((a, b) => (Number(b.menge) || 0) - (Number(a.menge) || 0));
        case "farbnummer":
            return liste.sort((a, b) => (a.farbnummer ?? 999999) - (b.farbnummer ?? 999999));
        case "dringlichkeit":
        default:
            return liste.sort((a, b) => (DRINGLICHKEIT_RANG[a.dringlichkeit] ?? 9) - (DRINGLICHKEIT_RANG[b.dringlichkeit] ?? 9));
    }
}

const VORSCHLAG_TABELLENKOPF_HILFE = {
    artikel: "Farbton mit Artikelnummer, ggf. verlinkt zum Artikel-Blatt.",
    menge: "Vorgeschlagene Bestellmenge in Stück.",
    paletten: "Vorgeschlagene Bestellmenge, umgerechnet auf volle Paletten.",
    dringlichkeit: "Wie dringend die Bestellung ist: überfällig, sofort, bald oder geplant.",
    ankunft: "Kalenderwoche, in der die bestellte Ware ankommen würde.",
    begruendung: "Warum genau diese Menge und dieser Termin vorgeschlagen werden.",
    oskar: "Vergleichswert aus Oskars Bestellrechner (Excel) – Vergleichsmaßstab, nicht Vorlage.",
    status: "Offen, übernommen oder verworfen.",
    zustaendig: "Wer den Vorschlag bearbeitet – hier zuweisbar.",
    aktionen: "Übernehmen, verwerfen oder auf „offen“ zurücksetzen.",
};

/** Ein LKW innerhalb einer Lieferwoche (T16): Ladung/Kapazität/Prozent-Balken, überladen rot, aufklappbar. */
function LkwGruppe({lkw, artikelNachId, benutzer, canOrder, canArtikel, onZeileGeaendert}) {
    // Befund N1 (Rückmeldung 22.09.2026, Folgebefund zu S16): ein LKW kann
    // vollständig aus einer bereits bestehenden, geplanten Lieferung bestehen
    // (ladung_geplant > 0, vorschlag_ids leer) - dann gibt es hier keine
    // offenen Vorschlagszeilen zu zeigen. Die Zahl im Kopf (Ladung/Prozent)
    // ist dabei korrekt, nur eine leere Tabelle mit bloßen Spaltenköpfen
    // wirkte wie ein Fehler. So ein LKW klappt nicht von selbst auf und
    // zeigt aufgeklappt einen erklärenden Satz statt der Tabelle.
    const hatOffeneVorschlaege = (lkw.vorschlag_ids || []).length > 0;
    const [offen, setOffen] = useState(hatOffeneVorschlaege);
    const [sortierung, setSortierung] = useState("dringlichkeit");

    const artikel = (lkw.vorschlag_ids || []).map((id) => artikelNachId.get(id)).filter(Boolean);
    const sortiert = useMemo(() => sortiereArtikel(artikel, sortierung), [artikel, sortierung]);

    const prozent = Number(lkw.prozent) || 0;
    const ueberladen = prozent > 100;
    const balkenFarbe = ueberladen ? "bg-danger" : "bg-success";

    // Palettenstellplätze für die interaktive Lastwagen-Darstellung
    // (LkwLadung.jsx): ein Eintrag je offenem Vorschlag (farblich nach
    // Produktlinie) plus - falls dieser LKW schon teilweise aus einer
    // bestehenden Lieferung besteht - ein zusammengefasster grauer Block,
    // weil deren Aufschlüsselung nach Marke hier nicht vorliegt (nur die
    // Summe "ladung_geplant").
    const paletteStueck = Number(lkw.palette_stueck) || 0;
    const kapazitaetPaletten = paletteStueck > 0 ? Number(lkw.kapazitaet) / paletteStueck : null;
    const ladungsEintraege = useMemo(() => {
        const eintraege = artikel.map((zeile) => ({
            id: zeile.id,
            label: `${zeile.farbnummer !== null && zeile.farbnummer !== undefined ? formatFarbnummer(zeile.farbnummer, zeile.farbnummer_unter) + " " : ""}${zeile.farbname || zeile.artikel_nummer}`,
            unterlabel: zeile.artikel_nummer,
            menge: zeile.menge,
            paletten: zeile.paletten,
            farbwert: zeile.farbwert,
            gruppe: zeile.produktlinie || "unbekannt",
            gruppeLabel: produktlinieMarkeLabel(zeile.produktlinie),
            farbe: produktlinieFarbe(zeile.produktlinie),
            bezug: `Bestellvorschlag · Ankunft ${formatKw(zeile.ankunft_kw)} · ${dringlichkeitLabel(zeile.dringlichkeit)}`,
        }));

        if (Number(lkw.ladung_geplant) > 0) {
            eintraege.push({
                id: `geplant-${lkw.nr}`,
                label: "Bereits geplante Lieferung",
                menge: lkw.ladung_geplant,
                paletten: lkw.ladung_geplant_paletten,
                gruppe: "geplant",
                gruppeLabel: "Bereits geplant",
                farbe: LADUNG_FARBE_GEPLANT,
                bezug: lkw.positionen_geplant ? `${formatMenge(lkw.positionen_geplant)} Positionen, keine Aufschlüsselung nach Marke.` : "Keine Aufschlüsselung nach Marke.",
            });
        }

        return eintraege;
    }, [artikel, lkw.ladung_geplant, lkw.ladung_geplant_paletten, lkw.positionen_geplant, lkw.nr]);

    return (
        <Card className="mb-2 border-0 bg-light">
            <button
                type="button"
                className="btn btn-link text-decoration-none text-body d-flex align-items-center gap-3 w-100 text-start px-2 py-1"
                aria-expanded={offen}
                onClick={() => setOffen((o) => !o)}
            >
                <span className="fw-semibold">LKW {lkw.nr}</span>
                <div className="flex-grow-1" style={{maxWidth: 200}}>
                    <div className="progress" style={{height: 12}}>
                        <div className={`progress-bar ${balkenFarbe}`} style={{width: `${Math.min(100, prozent)}%`}} />
                    </div>
                </div>
                <span className={`small text-nowrap ${ueberladen ? "text-danger fw-semibold" : "text-muted"}`}>
                    {ueberladen ? `überladen: ${formatProzent(prozent)}` : formatProzent(prozent)}
                </span>
                <span className="small text-muted text-nowrap">
                    {formatMenge(lkw.ladung)} / {formatMenge(lkw.kapazitaet)} Dosen · Rest {formatMenge(lkw.rest_dosen)} ({formatFaktor(lkw.rest_paletten, 1)} Pal.)
                </span>
                <span className="ms-auto">{offen ? <PiCaretUp aria-hidden="true" /> : <PiCaretDown aria-hidden="true" />}</span>
            </button>

            {offen && (
                <div className="px-2 pb-2" onClick={(e) => e.stopPropagation()}>
                    <div className="mb-3">
                        <LkwLadung kapazitaetPaletten={kapazitaetPaletten} eintraege={ladungsEintraege} />
                    </div>
                </div>
            )}

            {offen && !hatOffeneVorschlaege && (
                <div className="px-2 pb-2" onClick={(e) => e.stopPropagation()}>
                    <p className="text-muted small mb-0">
                        Bereits bestellt – für diesen LKW liegen keine offenen Bestellvorschläge vor. Die Ladung
                        stammt aus einer bereits geplanten Lieferung
                        {lkw.positionen_geplant ? ` mit ${formatMenge(lkw.positionen_geplant)} Positionen` : ""}.
                    </p>
                </div>
            )}

            {offen && hatOffeneVorschlaege && (
                <div className="px-2 pb-2" onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="small text-muted">Sortierung</span>
                            <SelectList value={sortierung} options={SORT_OPTIONEN} onChange={setSortierung} size="sm" ariaLabel={`Sortierung LKW ${lkw.nr}`} style={{minWidth: 180}} />
                        </div>
                        <OverlayTrigger
                            flip
                            popperConfig={TOOLTIP_POPPER_CONFIG}
                            placement="left"
                            overlay={
                                <Tooltip id={`lkw-bestellung-${lkw.nr}`}>
                                    Kommt nach der Abstimmung mit dem Einkauf - dann wird hier aus diesem LKW direkt eine
                                    Einkaufsbestellung in Weclapp angelegt.
                                </Tooltip>
                            }
                        >
                            <span>
                                <button type="button" className="btn btn-sm btn-outline-primary" disabled>
                                    Als Bestellung in Weclapp anlegen
                                </button>
                            </span>
                        </OverlayTrigger>
                    </div>

                    <Table responsive size="sm" className="align-middle mb-0">
                        <thead>
                            <tr>
                                <th />
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.artikel}>Artikel</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.menge}>Menge</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.paletten}>Paletten</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.dringlichkeit}>Dringlichkeit</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.ankunft}>Ankunft</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.begruendung}>Begründung</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.oskar}>Oskars Vergleichswert</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.status}>Status</HeaderWithHelp>
                                </th>
                                <th>
                                    <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.zustaendig}>Zuständig</HeaderWithHelp>
                                </th>
                                {canOrder && (
                                    <th>
                                        <HeaderWithHelp help={VORSCHLAG_TABELLENKOPF_HILFE.aktionen}>Aktionen</HeaderWithHelp>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortiert.map((zeile) => (
                                <VorschlagZeile key={zeile.id} zeile={zeile} benutzer={benutzer} canOrder={canOrder} canArtikel={canArtikel} onGeaendert={onZeileGeaendert} />
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </Card>
    );
}

/**
 * Eine aufklappbare Lieferwoche (T16: "gruppiert nach Kalenderwoche und
 * nach LKW"): Kopf mit KW, Montag-Datum und Zielort, darin je LKW eine
 * eigene aufklappbare Gruppe mit Ladungs-Balken und Artikeltabelle.
 */
export default function VorschlaegeLieferwoche({lieferwoche, artikelNachId, benutzer, canOrder, canArtikel, onZeileGeaendert, standardOffen = false}) {
    const [offen, setOffen] = useState(standardOffen);
    const montag = lieferwoche.montag ? formatDatum(lieferwoche.montag) : null;

    return (
        <Card className="mb-2 shadow-sm">
            <button
                type="button"
                className="btn btn-link text-decoration-none text-body d-flex align-items-center gap-3 w-100 text-start px-3 py-2"
                aria-expanded={offen}
                onClick={() => setOffen((o) => !o)}
            >
                <span className="fw-semibold">{formatKw(lieferwoche.kw)}</span>
                {montag && <span className="small text-muted">Montag {montag}</span>}
                {lieferwoche.zielort && <span className="small text-muted">{lieferwoche.zielort}</span>}
                <span className="small text-muted">
                    {formatMenge(lieferwoche.summe)} Dosen · {lieferwoche.anzahl_lkw ?? (lieferwoche.lkws || []).length} LKW
                </span>
                <span className="ms-auto">{offen ? <PiCaretUp aria-hidden="true" /> : <PiCaretDown aria-hidden="true" />}</span>
            </button>

            {offen && (
                <Card.Body className="pt-2">
                    {(lieferwoche.lkws || []).map((lkw) => (
                        <LkwGruppe
                            key={lkw.nr}
                            lkw={lkw}
                            artikelNachId={artikelNachId}
                            benutzer={benutzer}
                            canOrder={canOrder}
                            canArtikel={canArtikel}
                            onZeileGeaendert={onZeileGeaendert}
                        />
                    ))}
                </Card.Body>
            )}
        </Card>
    );
}
